import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, PackageOpen, Truck } from "lucide-react";
import { api, apiErr, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import OrderStatusPill from "@/components/orders/OrderStatusPill";

const BRAND = "#d81f26";
const NEXT = { Confirmed: "Processing", Processing: "Packed", Packed: "Ready", Ready: "Out for delivery", "Out for delivery": "Delivered", Pending: "Confirmed" };
const DONE = ["Delivered", "Cancelled", "Returned", "Refunded"];

export default function SellerOrders() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [subs, setSubs] = useState([]);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/seller/suborders").then((r) => setSubs(r.data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); });
  }, []);
  useEffect(() => { if (user === false) nav("/login?next=/seller/orders"); }, [user, nav]);
  useEffect(() => { if (user) load(); }, [user, load]);

  const advance = async (sub, status) => {
    setBusy(sub.id);
    try { await api.put(`/seller/suborders/${sub.id}/status`, { status }); toast.success(`→ ${status}`); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };

  if (denied) return <div className="max-w-md mx-auto text-center py-24"><PackageOpen size={40} className="mx-auto text-slate-300 mb-3" /><p className="text-slate-500">Only approved sellers can manage orders. <button onClick={() => nav("/become-seller")} className="font-bold" style={{ color: BRAND }}>Apply here</button></p></div>;

  const pending = subs.filter((s) => !DONE.includes(s.status));
  const closed = subs.filter((s) => DONE.includes(s.status));

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="seller-orders-page">
      <button onClick={() => nav("/seller")} className="flex items-center gap-1 text-slate-500 text-sm font-medium mb-3"><ChevronLeft size={16} /> Seller Dashboard</button>
      <div className="flex items-center gap-2 mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: BRAND }}><Truck size={20} /></div>
        <div><h1 className="text-2xl font-extrabold">My Orders</h1><p className="text-slate-500 text-sm">Apne items ke fulfilment orders yahan process karein</p></div>
      </div>

      {subs.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="seller-no-orders">Abhi tak koi order nahi aaya.</div>
      ) : (
        <div className="space-y-6">
          {pending.length > 0 && (
            <section>
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-2">Active ({pending.length})</h2>
              <div className="space-y-3">{pending.map((s) => <SubCard key={s.id} sub={s} onAdvance={advance} busy={busy === s.id} />)}</div>
            </section>
          )}
          {closed.length > 0 && (
            <section>
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-2">Completed ({closed.length})</h2>
              <div className="space-y-3">{closed.map((s) => <SubCard key={s.id} sub={s} onAdvance={advance} busy={false} />)}</div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

const SubCard = ({ sub, onAdvance, busy }) => {
  const next = NEXT[sub.status];
  const closed = DONE.includes(sub.status);
  return (
    <div data-testid={`suborder-${sub.id}`} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="font-extrabold text-slate-900">{sub.suborder_no}</p>
          <p className="text-xs text-slate-400">Order {sub.order_no} · {sub.customer_name || "Customer"} · {new Date(sub.created_at).toLocaleDateString("en-IN")}</p>
        </div>
        <OrderStatusPill status={sub.status} testid={`suborder-status-${sub.id}`} />
      </div>
      <div className="border border-slate-100 rounded-xl divide-y divide-slate-50 mt-3">
        {sub.items.map((it) => (
          <div key={it.product_id} className="flex items-center justify-between p-2">
            <div className="flex items-center gap-2 min-w-0">
              {it.image ? <img src={it.image} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0" /> : <div className="w-9 h-9 rounded-lg bg-slate-100 shrink-0" />}
              <span className="text-sm truncate">{it.name} × {it.quantity}</span>
            </div>
            <span className="font-semibold text-sm shrink-0">{inr(it.line_total)}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-sm font-bold">Subtotal: {inr(sub.subtotal)}</span>
        {!closed && next && (
          <div className="flex gap-2">
            <button data-testid={`suborder-cancel-${sub.id}`} disabled={busy} onClick={() => onAdvance(sub, "Cancelled")} className="px-3 py-1.5 border border-rose-200 text-rose-600 rounded-lg text-xs font-bold hover:bg-rose-50 disabled:opacity-50">Cancel</button>
            <button data-testid={`suborder-advance-${sub.id}`} disabled={busy} onClick={() => onAdvance(sub, next)} className="px-3 py-1.5 text-white rounded-lg text-xs font-bold disabled:opacity-50" style={{ backgroundColor: BRAND }}>{busy ? "…" : `→ ${next}`}</button>
          </div>
        )}
      </div>
    </div>
  );
};

